import { Box, Tab, Tabs } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useContext, useState } from "react";
import useAPI from "../../../../../api/hooks/useAPI";
import useColors from "../../../../../api/hooks/useColors";
import { entityKeys } from "../../../../../api/queryKeys";
import CustomToolTip from "../../../../../components/CustomTooltip";
import LoadingIcon from "../../../../../components/LoadingIcon";
import { EnvFeatureFlagContext } from "../../../../../contexts/EnvFeaturesContext";
import {
  ComplexEntityTypes,
  ENTITY_TABS,
} from "../../../../../utils/constants";
import { entityHasAnyRawData } from "../../entitiesTable/fieldUtils";
import { useEntities } from "../../hooks/useEntities";
import PanelTab from "../PanelTab";
import AppDetailsView from "../views/app/AppDetailsView";
import AppHistoryView from "../views/app/AppHistoryView";
import AppTokensView from "../views/app/AppTokensView";
import AppUsersView from "../views/app/AppUserView";
import { appHasHistory, appHasUsers } from "../views/app/checkRowsApp";
import {
  dataStoreHasActivity,
  dataStoreHasHistory,
  dataStoreHasTags,
} from "../views/dataStore/checkRowsDataStore";
import DataStoreActivityView from "../views/dataStore/DataStoreActivityView";
import DataStoreDetailsView from "../views/dataStore/DataStoreDetailsView";
import DataStoreHistoryView from "../views/dataStore/DataStoreHistoryView";
import TagsDataStoreView from "../views/dataStore/TagsDataStoreView";
import EntityRawDataView from "../views/EntityRawDataView";
import {
  getHostGroupsRows,
  getHostLoginsRows,
  getHostPoliciesRows,
  hostHasClassificationData,
  hostHasComplianceData,
  hostHasInstalledPrograms,
  hostHasOwnershipData,
  hostHasSecurityGroups,
  hostHasTags,
  hostHistoryDisabled,
} from "../views/host/checkRowsHostData";
import HostClassificationView from "../views/host/HostClassificationView";
import HostComplianceView from "../views/host/HostComplianceView";
import HostDetailsView from "../views/host/HostDetailsView";
import HostGroupsView from "../views/host/HostGroupsView";
import HostHistoryView from "../views/host/HostHistoryView";
import HostLoginsView from "../views/host/HostLoginsView";
import HostOwnershipView from "../views/host/HostOwnershipView";
import HostPoliciesView from "../views/host/HostPoliciesView";
import HostSecurityGroupsView from "../views/host/HostSecurityGroupsView";
import TagsHostView from "../views/host/TagsHostView";
import InstalledProgramsView from "../views/InstalledProgramsView";
import useUserById, {
  getNHIPoliciesRows,
  nonHumanIdentityHasCredentials,
  nonHumanIdentityHasHistory,
  nonHumanIdentityHasTags,
} from "../views/nonhuman_identity/checkNonHumanIdentityData";
import { getNHIRolesRows } from "../views/nonhuman_identity/checkRowsNonHumanIdentityData";
import NHIPoliciesView from "../views/nonhuman_identity/NHIPoliciesView";
import NonHumanIdentityCredentialsView from "../views/nonhuman_identity/NonHumanIdentityCredentialsView";
import NonHumanIdentityDetailsView from "../views/nonhuman_identity/NonHumanIdentityDetailsView";
import NonHumanIdentityHistoryView from "../views/nonhuman_identity/NonHumanIdentityHistoryView";
import NonHumanIdentityRolesView from "../views/nonhuman_identity/NonHumanIdentityRolesView";
import TagsNonHumanIdentityView from "../views/nonhuman_identity/TagsNonHumanIdentityView";
import {
  getServerCreationDateRows,
  getServerGroupsRows,
  getServerLastLaunchDateRows,
  getServerLastModifiedDateRows,
  getServerLastScanDateRows,
  getServerLastSeenRows,
  getServerLastUpdatedDateRows,
  getServerLoginsRows,
  getServerPoliciesRows,
  getServerPurchaseDateRows,
  serverHasClassificationData,
  serverHasCodeRepository,
  serverHasComplianceData,
  serverHasOwnershipData,
  serverHasSecurityGroups,
  serverHasTags,
} from "../views/server/checkRowsServerData";
import ServerClassificationView from "../views/server/ServerClassificationView";
import ServerCodeRepositoryView from "../views/server/ServerCodeRepositoryView";
import ServerComplianceView from "../views/server/ServerComplianceView";
import ServerDetailsView from "../views/server/ServerDetailsView";
import ServerGroupsView from "../views/server/ServerGroupsView";
import ServerHistoryView from "../views/server/ServerHistoryView";
import ServerLoginsView from "../views/server/ServerLoginsView";
import ServerOwnershipView from "../views/server/ServerOwnershipView";
import ServerPoliciesView from "../views/server/ServerPoliciesView";
import ServerSecurityGroupsView from "../views/server/ServerSecurityGroupsView";
import TagsServerView from "../views/server/TagsServerView";
import { deviceHasAnyUsers } from "../views/shared/UsersView/usersRows";
import UsersView from "../views/shared/UsersView/UsersView";
import {
  getUserCreationDateRows,
  getUserGroupsRows,
  getUserLastPasswordChangeRows,
  getUserLastSeenRows,
  getUserLastUpdateDateRows,
  getUserMFARows,
  getUserPoliciesRows,
  getUserRelatedAppsRows,
  getUserRolesRows,
} from "../views/user/checkRowsUserData";
import UserAppView from "../views/user/UserAppView";
import UserDetailsView from "../views/user/UserDetailsView";
import UserGroupsView from "../views/user/UserGroupsView";
import UserLastPasswordChangeView from "../views/user/UserLastPasswordChangeView";
import UserLastAndFirstSeenView from "../views/user/UserLastSeenView";
import UserMFAView from "../views/user/UserMFAView";
import UserPoliciesView from "../views/user/UserPoliciesView";
import UserRolesView from "../views/user/UserRolesView";
import LabelWithIcon from "./LabelWithIcon";

export default function TabsView({ selectedEntity }) {
  const [valueTab, setValueTab] = useState(0);
  const user = useUserById(selectedEntity);
  const { trackEvent, fetchEntityRawDataRds } = useAPI();
  const { useQueryForEntitiesByType } = useEntities();
  const { isActiveRds } = useContext(EnvFeatureFlagContext);
  const { data: userEntities } = useQueryForEntitiesByType({
    type: ComplexEntityTypes.USER,
  });

  const { data: appEntities } = useQueryForEntitiesByType({
    type: ComplexEntityTypes.APP,
  });

  const handleTabChange = (event, newValue) => {
    trackEvent(
      `User changed tab in the drawer of the entity: ${selectedEntity.display_name}, type: ${selectedEntity?.entity_type}`
    );
    // TODO this may break since it depends on the lists below...
    const ENTITY_TAB_INDEX = 1; // always after Properties, starting to count from 0
    if (newValue === ENTITY_TAB_INDEX) {
      // NOTE: this track message is monitored by Mixpanel to collect data on user engagement - DO NOT CHANGE IT
      trackEvent(
        `User opened entity Risk tab for entity id ${selectedEntity.entity_id}`
      );
    }
    setValueTab(newValue);
  };

  const colors = useColors();
  const appHasTokens =
    selectedEntity?.salesforce_data?.oauth_tokens &&
    selectedEntity?.salesforce_data?.oauth_tokens?.length > 0;

  const entityRawDataQuery = useQuery({
    queryKey: entityKeys.entityRawData(selectedEntity?.entity_id),
    queryFn: () =>
      fetchEntityRawDataRds(
        selectedEntity.customer_id,
        selectedEntity.entity_id
      ),

    select: (response) => response.data,
    enabled: Boolean(selectedEntity?.entity_id && isActiveRds),
  });

  if (entityRawDataQuery.isSuccess)
    selectedEntity = { ...selectedEntity, ...entityRawDataQuery.data };

  const viewsPerEntityType = {
    host: [
      {
        viewName: ENTITY_TABS.PROPERTIES,
        label: <LabelWithIcon type={ENTITY_TABS.PROPERTIES} />,
        view: HostDetailsView,
      },
      {
        viewName: ENTITY_TABS.HISTORY,
        label: <LabelWithIcon type={ENTITY_TABS.HISTORY} />,
        view: HostHistoryView,
        disabled: hostHistoryDisabled(selectedEntity),
      },
      {
        viewName: ENTITY_TABS.COMPLIANCE,
        label: <LabelWithIcon type={ENTITY_TABS.COMPLIANCE} />,
        view: HostComplianceView,
        disabled: !hostHasComplianceData(selectedEntity),
      },
      {
        viewName: ENTITY_TABS.CLASSIFICATION,
        label: <LabelWithIcon type={ENTITY_TABS.CLASSIFICATION} />,
        view: HostClassificationView,
        disabled: !hostHasClassificationData(selectedEntity),
      },
      {
        viewName: ENTITY_TABS.OWNERSHIP,
        label: <LabelWithIcon type={ENTITY_TABS.OWNERSHIP} />,
        view: HostOwnershipView,
        disabled: !hostHasOwnershipData(selectedEntity),
      },
      {
        viewName: ENTITY_TABS.POLICIES,
        label: <LabelWithIcon type={ENTITY_TABS.POLICIES} />,
        view: HostPoliciesView,
        disabled: getHostPoliciesRows(selectedEntity).length <= 0,
      },
      {
        viewName: ENTITY_TABS.USERS,
        label: <LabelWithIcon type={ENTITY_TABS.USERS} />,
        view: UsersView,
        disabled: !deviceHasAnyUsers(selectedEntity),
      },
      {
        viewName: ENTITY_TABS.GROUPS,
        label: <LabelWithIcon type={ENTITY_TABS.GROUPS} />,
        view: HostGroupsView,
        disabled: getHostGroupsRows(selectedEntity).length <= 0,
      },
      {
        viewName: ENTITY_TABS.SECURITY_GROUPS,
        label: <LabelWithIcon type={ENTITY_TABS.SECURITY_GROUPS} />,
        view: HostSecurityGroupsView,
        disabled: !hostHasSecurityGroups(selectedEntity),
      },
      {
        viewName: ENTITY_TABS.LOGINS,
        label: <LabelWithIcon type={ENTITY_TABS.LOGINS} />,
        view: HostLoginsView,
        disabled: getHostLoginsRows(selectedEntity).length <= 0,
      },
      {
        viewName: ENTITY_TABS.TAGS,
        label: <LabelWithIcon type={ENTITY_TABS.TAGS} />,
        view: TagsHostView,
        disabled: !hostHasTags(selectedEntity),
      },
      ...(isActiveRds
        ? [
            {
              viewName: ENTITY_TABS.INSTALLED_PROGRAMS,
              label: <LabelWithIcon type={ENTITY_TABS.INSTALLED_PROGRAMS} />,
              view: InstalledProgramsView,
              disabled: !hostHasInstalledPrograms(selectedEntity),
            },
            {
              viewName: ENTITY_TABS.RAW_DATA,
              label: entityRawDataQuery.isLoading ? (
                <LoadingIcon size={4} />
              ) : (
                <LabelWithIcon type={ENTITY_TABS.RAW_DATA} />
              ),
              view: EntityRawDataView,
              disabled:
                entityRawDataQuery.isError ||
                (entityRawDataQuery.isSuccess &&
                  !entityHasAnyRawData(selectedEntity)),
            },
          ]
        : []),
    ],
    user: [
      {
        viewName: ENTITY_TABS.PROPERTIES,
        label: <LabelWithIcon type={ENTITY_TABS.PROPERTIES} />,
        view: UserDetailsView,
      },

      {
        viewName: ENTITY_TABS.PASSWORD_TRACKING,
        label: <LabelWithIcon type={ENTITY_TABS.PASSWORD_TRACKING} />,
        view: UserLastPasswordChangeView,
        disabled: getUserLastPasswordChangeRows(selectedEntity).length <= 0,
      },
      {
        viewName: ENTITY_TABS.HISTORY,
        label: <LabelWithIcon type={ENTITY_TABS.HISTORY} />,
        view: UserLastAndFirstSeenView,
        disabled:
          getUserLastSeenRows(selectedEntity)?.length <= 0 &&
          getUserCreationDateRows(selectedEntity)?.length <= 0 &&
          getUserLastUpdateDateRows(selectedEntity)?.length <= 0,
      },
      {
        viewName: ENTITY_TABS.MFA,
        label: <LabelWithIcon type={ENTITY_TABS.MFA} />,
        view: UserMFAView,
        disabled: getUserMFARows(selectedEntity).length <= 0,
      },
      {
        viewName: ENTITY_TABS.POLICIES,
        label: <LabelWithIcon type={ENTITY_TABS.POLICIES} />,
        view: UserPoliciesView,
        disabled: getUserPoliciesRows(selectedEntity).length <= 0,
      },
      {
        viewName: ENTITY_TABS.GROUPS,
        label: <LabelWithIcon type={ENTITY_TABS.GROUPS} />,
        view: UserGroupsView,
        disabled: getUserGroupsRows(selectedEntity).length <= 0,
      },
      {
        viewName: ENTITY_TABS.ROLES,
        label: <LabelWithIcon type={ENTITY_TABS.ROLES} />,
        view: UserRolesView,
        disabled: getUserRolesRows(selectedEntity).length <= 0,
      },
      {
        viewName: ENTITY_TABS.APPS,
        label: <LabelWithIcon type={ENTITY_TABS.APPS} />,
        view: UserAppView,
        disabled:
          getUserRelatedAppsRows(selectedEntity, appEntities).length <= 0,
      },
      ...(isActiveRds
        ? [
            {
              viewName: ENTITY_TABS.RAW_DATA,
              label: entityRawDataQuery.isLoading ? (
                <LoadingIcon size={4} />
              ) : (
                <LabelWithIcon type={ENTITY_TABS.RAW_DATA} />
              ),
              view: EntityRawDataView,
              disabled:
                entityRawDataQuery.isError ||
                (entityRawDataQuery.isSuccess &&
                  !entityHasAnyRawData(selectedEntity)),
            },
          ]
        : []),
    ],
    server: [
      {
        viewName: ENTITY_TABS.PROPERTIES,
        label: <LabelWithIcon type={ENTITY_TABS.PROPERTIES} />,
        view: ServerDetailsView,
      },
      {
        viewName: ENTITY_TABS.HISTORY,
        label: <LabelWithIcon type={ENTITY_TABS.HISTORY} />,
        view: ServerHistoryView,
        disabled:
          getServerCreationDateRows(selectedEntity).length <= 0 &&
          getServerLastSeenRows(selectedEntity)?.length <= 0 &&
          getServerLastLaunchDateRows(selectedEntity).length <= 0 &&
          getServerLastModifiedDateRows(selectedEntity).length <= 0 &&
          getServerLastScanDateRows(selectedEntity).length <= 0 &&
          getServerLastUpdatedDateRows(selectedEntity).length <= 0 &&
          getServerPurchaseDateRows(selectedEntity).length <= 0,
      },
      {
        viewName: ENTITY_TABS.COMPLIANCE,
        label: <LabelWithIcon type={ENTITY_TABS.COMPLIANCE} />,
        view: ServerComplianceView,
        disabled: !serverHasComplianceData(selectedEntity),
      },
      {
        viewName: ENTITY_TABS.CLASSIFICATION,
        label: <LabelWithIcon type={ENTITY_TABS.CLASSIFICATION} />,
        view: ServerClassificationView,
        disabled: !serverHasClassificationData(selectedEntity),
      },
      {
        viewName: ENTITY_TABS.OWNERSHIP,
        label: <LabelWithIcon type={ENTITY_TABS.OWNERSHIP} />,
        view: ServerOwnershipView,
        disabled: !serverHasOwnershipData(selectedEntity),
      },
      {
        viewName: ENTITY_TABS.GROUPS,
        label: <LabelWithIcon type={ENTITY_TABS.GROUPS} />,
        view: ServerGroupsView,
        disabled: getServerGroupsRows(selectedEntity)?.length <= 0,
      },
      {
        viewName: ENTITY_TABS.POLICIES,
        label: <LabelWithIcon type={ENTITY_TABS.POLICIES} />,
        view: ServerPoliciesView,
        disabled: getServerPoliciesRows(selectedEntity).length <= 0,
      },
      {
        viewName: ENTITY_TABS.TAGS,
        label: <LabelWithIcon type={ENTITY_TABS.TAGS} />,
        view: TagsServerView,
        disabled: !serverHasTags(selectedEntity),
      },
      {
        viewName: ENTITY_TABS.REPOSITORY,
        label: <LabelWithIcon type={ENTITY_TABS.REPOSITORY} />,
        view: ServerCodeRepositoryView,
        disabled: !serverHasCodeRepository(selectedEntity),
      },
      {
        viewName: ENTITY_TABS.USERS,
        label: <LabelWithIcon type={ENTITY_TABS.USERS} />,
        view: UsersView,
        disabled: !deviceHasAnyUsers(selectedEntity),
      },
      {
        viewName: ENTITY_TABS.SECURITY_GROUPS,
        label: <LabelWithIcon type={ENTITY_TABS.SECURITY_GROUPS} />,
        view: ServerSecurityGroupsView,
        disabled: !serverHasSecurityGroups(selectedEntity),
      },
      {
        viewName: ENTITY_TABS.LOGINS,
        label: <LabelWithIcon type={ENTITY_TABS.LOGINS} />,
        view: ServerLoginsView,
        disabled: getServerLoginsRows(selectedEntity).length <= 0,
      },
      ...(isActiveRds
        ? [
            {
              viewName: ENTITY_TABS.RAW_DATA,
              label: entityRawDataQuery.isLoading ? (
                <LoadingIcon size={4} />
              ) : (
                <LabelWithIcon type={ENTITY_TABS.RAW_DATA} />
              ),
              view: EntityRawDataView,
              disabled:
                entityRawDataQuery.isError ||
                (entityRawDataQuery.isSuccess &&
                  !entityHasAnyRawData(selectedEntity)),
            },
          ]
        : []),
    ],
    data_store: [
      {
        viewName: ENTITY_TABS.PROPERTIES,
        label: <LabelWithIcon type={ENTITY_TABS.PROPERTIES} />,
        view: DataStoreDetailsView,
      },
      {
        viewName: ENTITY_TABS.HISTORY,
        label: <LabelWithIcon type={ENTITY_TABS.HISTORY} />,
        view: DataStoreHistoryView,
        disabled: !dataStoreHasHistory(selectedEntity),
      },
      {
        viewName: ENTITY_TABS.TAGS,
        label: <LabelWithIcon type={ENTITY_TABS.TAGS} />,
        view: TagsDataStoreView,
        disabled: !dataStoreHasTags(selectedEntity),
      },
      {
        viewName: ENTITY_TABS.ACTIVITY,
        label: <LabelWithIcon type={ENTITY_TABS.ACTIVITY} />,
        view: DataStoreActivityView,
        disabled: !dataStoreHasActivity(selectedEntity),
      },
      ...(isActiveRds
        ? [
            {
              viewName: ENTITY_TABS.RAW_DATA,
              label: entityRawDataQuery.isLoading ? (
                <LoadingIcon size={4} />
              ) : (
                <LabelWithIcon type={ENTITY_TABS.RAW_DATA} />
              ),
              view: EntityRawDataView,
              disabled:
                entityRawDataQuery.isError ||
                (entityRawDataQuery.isSuccess &&
                  !entityHasAnyRawData(selectedEntity)),
            },
          ]
        : []),
    ],
    nonhuman_identity: [
      {
        viewName: ENTITY_TABS.PROPERTIES,
        label: <LabelWithIcon type={ENTITY_TABS.PROPERTIES} />,
        view: NonHumanIdentityDetailsView,
      },
      {
        viewName: ENTITY_TABS.HISTORY,
        label: <LabelWithIcon type={ENTITY_TABS.HISTORY} />,
        view: NonHumanIdentityHistoryView,
        disabled: !nonHumanIdentityHasHistory(selectedEntity),
      },
      {
        viewName: ENTITY_TABS.POLICIES,
        label: <LabelWithIcon type={ENTITY_TABS.POLICIES} />,
        view: NHIPoliciesView,
        disabled: getNHIPoliciesRows(selectedEntity).length <= 0,
      },
      {
        viewName: ENTITY_TABS.ROLES,
        label: <LabelWithIcon type={ENTITY_TABS.ROLES} />,
        view: NonHumanIdentityRolesView,
        disabled: user === null && getNHIRolesRows(selectedEntity).length <= 0,
      },
      {
        viewName: ENTITY_TABS.CREDENTIALS,
        label: <LabelWithIcon type={ENTITY_TABS.CREDENTIALS} />,
        view: NonHumanIdentityCredentialsView,
        disabled: !nonHumanIdentityHasCredentials(selectedEntity),
      },
      {
        viewName: ENTITY_TABS.TAGS,
        label: <LabelWithIcon type={ENTITY_TABS.TAGS} />,
        view: TagsNonHumanIdentityView,
        disabled: !nonHumanIdentityHasTags(selectedEntity),
      },
      ...(isActiveRds
        ? [
            {
              viewName: ENTITY_TABS.RAW_DATA,
              label: entityRawDataQuery.isLoading ? (
                <LoadingIcon size={4} />
              ) : (
                <LabelWithIcon type={ENTITY_TABS.RAW_DATA} />
              ),
              view: EntityRawDataView,
              disabled:
                entityRawDataQuery.isError ||
                (entityRawDataQuery.isSuccess &&
                  !entityHasAnyRawData(selectedEntity)),
            },
          ]
        : []),
    ],
    app: [
      {
        viewName: ENTITY_TABS.PROPERTIES,
        label: <LabelWithIcon type={ENTITY_TABS.PROPERTIES} />,
        view: AppDetailsView,
      },
      {
        viewName: ENTITY_TABS.HISTORY,
        label: <LabelWithIcon type={ENTITY_TABS.HISTORY} />,
        view: AppHistoryView,
        disabled: !appHasHistory(selectedEntity),
      },
      {
        viewName: ENTITY_TABS.USERS,
        label: <LabelWithIcon type={ENTITY_TABS.USERS} />,
        view: AppUsersView,
        disabled: !appHasUsers(selectedEntity, userEntities),
      },
      {
        viewName: ENTITY_TABS.CREDENTIALS,
        label: <LabelWithIcon type={ENTITY_TABS.CREDENTIALS} />,
        view: AppTokensView,
        disabled: !appHasTokens,
      },
      ...(isActiveRds
        ? [
            {
              viewName: ENTITY_TABS.RAW_DATA,
              label: entityRawDataQuery.isLoading ? (
                <LoadingIcon size={4} />
              ) : (
                <LabelWithIcon type={ENTITY_TABS.RAW_DATA} />
              ),
              view: EntityRawDataView,
              disabled:
                entityRawDataQuery.isError ||
                (entityRawDataQuery.isSuccess &&
                  !entityHasAnyRawData(selectedEntity)),
            },
          ]
        : []),
    ],
  };

  return (
    <Box sx={{ display: "flex", height: "100%", marginBottom: "80px" }}>
      <Box sx={{ borderRight: 1, borderColor: "divider" }}>
        <Tabs
          value={valueTab}
          orientation="vertical"
          onChange={handleTabChange}
          sx={{ padding: 2, paddingLeft: 0 }}
          TabIndicatorProps={{
            style: {
              display: "none",
            },
          }}
        >
          {viewsPerEntityType[selectedEntity?.entity_type]?.map((tab, index) =>
            tab.disabled && tab.viewName === ENTITY_TABS.RISK ? (
              <CustomToolTip
                placement={"left-start"}
                title={"No Gaps and Steps are applicable on this entity"}
              >
                <Box
                  component="span"
                  sx={{
                    display: "inline-block",
                    width: "100%",
                  }}
                >
                  <Tab
                    key={index}
                    label={tab.label}
                    disabled={tab.disabled}
                    sx={{
                      borderRadius: "8px",
                      textTransform: "none",
                      backgroundColor:
                        valueTab === index
                          ? colors.primary_8percent
                          : "transparent",
                      color: colors.contrast_text_dark,
                      minHeight: "20px",
                      padding: "5px 8px",
                      mb: 1,
                      opacity: 1 + " !important",
                      "&.Mui-selected": {
                        color: colors.primary_main,
                      },
                      "&.Mui-disabled": {
                        color: colors.action_disabled,
                      },
                      "&:hover": {
                        backgroundColor: colors.primary_16percent,
                        color: colors.primary_dark,
                      },
                    }}
                  />
                </Box>
              </CustomToolTip>
            ) : (
              <Tab
                key={index}
                label={tab.label}
                disabled={tab.disabled}
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  backgroundColor:
                    valueTab === index
                      ? colors.primary_8percent
                      : "transparent",
                  color: colors.contrast_text_dark,
                  minHeight: "20px",
                  padding: "5px 8px",
                  mb: 1,
                  "&.Mui-selected": {
                    color: colors.primary_main,
                  },
                  "&.Mui-disabled": {
                    color: colors.action_disabled,
                  },
                  "&:hover": {
                    backgroundColor: colors.primary_16percent,
                    color: colors.primary_dark,
                  },
                }}
              />
            )
          )}
        </Tabs>
      </Box>
      <Box sx={{ flexGrow: 1, pl: 2 }}>
        {viewsPerEntityType[selectedEntity?.entity_type]?.map((view, index) => {
          return (
            <PanelTab value={valueTab} index={index} key={index}>
              <view.view entityData={selectedEntity} />
            </PanelTab>
          );
        })}
      </Box>
    </Box>
  );
}
